import { z } from 'zod';
import { ComplianceRuleTypeSchema, ComplianceVerificationSchema, EstimateNoteKindSchema, EstimateSectionKindSchema, ProposalPresentationSchema, } from '../lib/enums';
import { zEmail, zHexColor, zIsoDateTime, zMoney, zPct, zQty, zSha256Hex, zStateCode, zUuid, } from './common';
import { PlacementSchema } from './compliance';
/**
 * Proposals. The proposal row is MUTABLE presentation config; each send
 * freezes a `proposal_renders.document` (the render model below) under the
 * same immutability regime as estimate versions.
 *
 * The render model is CLIENT-FACING: it carries PRICES only. Cost, markup and
 * margin internals are structurally absent — that privacy boundary is
 * enforced by this schema, not by view code.
 */
// ----------------------------------------------------------- proposal (config)
export const ProposalCoverSchema = z
    .object({
    title: z.string().max(300).nullable().optional(),
    subtitle: z.string().max(300).nullable().optional(),
    coverImageFileId: zUuid.nullable().optional(),
    introMd: z.string().max(20000).nullable().optional(),
    showLogo: z.boolean().optional(),
})
    .strict();
export const ProposalSchema = z.object({
    estimateId: zUuid,
    presentation: ProposalPresentationSchema,
    cover: ProposalCoverSchema,
    scopeNarrative: z.string().max(50000).nullable(),
    /** Compliance rule_keys selected for inclusion (filtered by site jurisdiction/work type/trade). */
    selectedRuleKeys: z.array(z.string().min(1).max(120)),
    updatedAt: zIsoDateTime,
});
export const UpdateProposalRequestSchema = z
    .object({
    presentation: ProposalPresentationSchema.optional(),
    cover: ProposalCoverSchema.optional(),
    scopeNarrative: z.string().max(50000).nullable().optional(),
    selectedRuleKeys: z.array(z.string().min(1).max(120)).max(50).optional(),
})
    .strict();
// ------------------------------------------------------------- render model
/**
 * Disclosure block snapshotted VERBATIM into the render, with its
 * verification label and mandatory boundary note.
 */
export const DisclosureBlockSchema = z
    .object({
    ruleKey: z.string().min(1).max(120),
    ruleType: ComplianceRuleTypeSchema,
    title: z.string().min(1).max(300),
    bodyMd: z.string().min(1),
    verification: ComplianceVerificationSchema,
    boundaryNote: z.string().min(1),
    placement: PlacementSchema,
    jurisdiction: z.string().min(2).max(2),
})
    .passthrough();
/** Client-facing priced line: price only — no cost/markup fields exist here. */
export const RenderLineSchema = z
    .object({
    description: z.string(),
    qty: zQty.nullable(),
    unit: z.string().nullable(),
    price: zMoney,
})
    .strict();
export const RenderSectionSchema = z
    .object({
    sectionId: zUuid,
    title: z.string(),
    kind: EstimateSectionKindSchema,
    alternateGroup: z.string().nullable(),
    /** Omitted (null) in lump_sum presentation. */
    lines: z.array(RenderLineSchema).nullable(),
    price: zMoney,
})
    .strict();
export const RenderPricingSchema = z
    .object({
    presentation: ProposalPresentationSchema,
    /** Base-scope sections (alternates listed separately below). */
    sections: z.array(RenderSectionSchema),
    /** Selectable alternates, priced separately from base. */
    alternates: z.array(RenderSectionSchema),
    /** Allowance line flagging note shown to the client, if any allowances exist. */
    allowanceNote: z.string().nullable(),
    subtotal: zMoney,
    tax: z
        .object({
        label: z.string(),
        ratePct: zPct,
        amount: zMoney,
    })
        .strict()
        .nullable(),
    total: zMoney,
})
    .strict();
export const RenderBrandSchema = z
    .object({
    companyName: z.string(),
    displayName: z.string().nullable(),
    logoFileId: zUuid.nullable(),
    primaryColor: zHexColor.nullable(),
    accentColor: zHexColor.nullable(),
    footerText: z.string().nullable(),
    /** License lines shown per license_display rules — contractor-entered, unverified. */
    licenseLines: z.array(z
        .object({
        jurisdiction: z.string(),
        licenseNumber: z.string(),
        classification: z.string().nullable(),
    })
        .strict()),
})
    .strict();
/**
 * The full frozen render document (`proposal_renders.document`).
 * `content_hash = canonicalJsonHash(document)`; the signed response's
 * `document_hash` is trigger-bound to it. Top level is passthrough so stored
 * documents re-parse byte-stable.
 */
export const RenderModelSchema = z
    .object({
    schemaVersion: z.literal(1),
    generatedAt: zIsoDateTime,
    brand: RenderBrandSchema,
    cover: z
        .object({
        title: z.string().nullable(),
        subtitle: z.string().nullable(),
        coverImageFileId: zUuid.nullable(),
        introMd: z.string().nullable(),
        showLogo: z.boolean(),
    })
        .strict(),
    client: z
        .object({
        name: z.string(),
        contactName: z.string().nullable(),
        siteAddress: z.string().nullable(),
        jurisdictionState: zStateCode.nullable(),
    })
        .strict(),
    estimateRef: z
        .object({
        estimateNumber: z.string(),
        versionId: zUuid,
        versionNumber: z.number().int().min(1),
        contentHash: zSha256Hex,
        title: z.string(),
    })
        .strict(),
    scopeNarrative: z.string().nullable(),
    pricing: RenderPricingSchema,
    notes: z.array(z
        .object({
        kind: EstimateNoteKindSchema,
        body: z.string(),
    })
        .strict()),
    disclosures: z.array(DisclosureBlockSchema),
    esign: z
        .object({
        /** ESIGN consent language captured before signing unlocks. */
        consentText: z.string().min(1),
    })
        .strict(),
    validUntil: zIsoDateTime.nullable(),
})
    .passthrough();
// ---------------------------------------------------------- preview + renders
export const ProposalPreviewResponseSchema = z.object({
    document: RenderModelSchema,
    /** Non-fatal issues, e.g. a license_display rule with no license record entered. */
    warnings: z.array(z.string()),
});
export const ProposalRenderSchema = z.object({
    id: zUuid,
    estimateVersionId: zUuid,
    contentHash: zSha256Hex,
    pdfFileId: zUuid.nullable(),
    createdAt: zIsoDateTime,
});
// -------------------------------------------------------------------- events
export const ProposalEventSchema = z.object({
    id: zUuid,
    renderId: zUuid,
    kind: z.enum(['viewed', 'pdf_downloaded', 'consent_given', 'signed_copy_viewed']),
    ip: z.string().max(64).nullable(),
    userAgent: z.string().max(500).nullable(),
    createdAt: zIsoDateTime,
});
/** Append-only response — one per render; hash-bound to the render document. */
export const ProposalResponseSchema = z.object({
    id: zUuid,
    renderId: zUuid,
    kind: z.enum(['accepted', 'declined']),
    signerName: z.string().nullable(),
    signerEmail: zEmail.nullable(),
    typedSignature: z.string().nullable(),
    alternatesSelected: z.array(zUuid),
    consentTextSnapshot: z.string().nullable(),
    consentGivenAt: zIsoDateTime.nullable(),
    intentConfirmedAt: zIsoDateTime.nullable(),
    signedAt: zIsoDateTime.nullable(),
    declineReason: z.string().nullable(),
    documentHash: zSha256Hex,
    ip: z.string().max(64).nullable(),
    userAgent: z.string().max(500).nullable(),
    createdAt: zIsoDateTime,
});
//# sourceMappingURL=proposals.js.map